import { CloseOutlined } from '@ant-design/icons';
import { evaluate } from 'mathjs';
import { useEffect, useState } from 'react';
import styles from './Calculator.module.less';
import DisplayWindow from './DisplayWindow';
import KeysWindow from './KeysWindow';

interface CalculatorProps {
  onClose?: () => void;
}

function Calculator({ onClose }: CalculatorProps) {
  const [expression, setExpression] = useState<string>('');
  const [displayEXP, setDisplayEXP] = useState<string>('');
  const [result, setResult] = useState<string | number>('0');

  const sciFunc: Record<string, string> = {
    sin: 'sin',
    cos: 'cos',
    tan: 'tan',
    ln: 'log',
    log: 'log10',
    π: 'pi',
    e: 'e',
    '^': '^',
    '√': 'sqrt',
  };

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      const { key } = event;

      if (key === 'Enter') {
        event.preventDefault();
      }

      if (key >= '0' && key <= '9') {
        handleButton(key);
      } else if (key === 'Enter' || key === '=') {
        handleButton('=');
      } else if (key === 'Backspace') {
        handleButton('DEL');
      } else if (key === 'Escape') {
        handleButton('AC');
      } else if (
        key === '+' ||
        key === '-' ||
        key === '*' ||
        key === '/' ||
        key === '.' ||
        key === '(' ||
        key === ')'
      ) {
        handleButton(key);
      } else if (key === 's') {
        handleButton('sin');
      } else if (key === 'c') {
        handleButton('cos');
      } else if (key === 't') {
        handleButton('tan');
      } else if (key === 'l') {
        handleButton('ln');
      } else if (key === 'L') {
        handleButton('log');
      } else if (key === 'p') {
        handleButton('π');
      } else if (key === 'e') {
        handleButton('e');
      } else if (key === '^') {
        handleButton('^');
      } else if (key === 'r') {
        handleButton('√');
      } else if (key === '!') {
        handleButton('!');
      }
    };

    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [expression, displayEXP]);

  function calcResult() {
    if (expression.length !== 0) {
      try {
        let compute = evaluate(expression);
        // compute = parseFloat(compute.toFixed(4));
        // Use standard JS Math or just toFixed if evaluate returns number.
        // mathjs evaluate can return various types. Assuming number here for simple calculator.
        // If compute is a number:
        if (typeof compute === 'number') {
          compute = parseFloat(compute.toFixed(4));
        }
        setResult(compute);
      } catch (error) {
        setResult('Error');
      }
    } else {
      setResult('An Error Occured!');
    }
  }

  function factorial(n: number): number {
    let res = 1;
    for (let i = 1; i <= n; i++) res *= i;
    return res;
  }

  function handleButton(value: string) {
    if (value === 'AC') {
      setExpression('');
      setDisplayEXP('');
      setResult('0');
    } else if (value === '=') {
      calcResult();
    } else if (value === 'DEL') {
      setDisplayEXP(displayEXP.slice(0, -1));
      setExpression(expression.slice(0, -1));
    } else if (Object.prototype.hasOwnProperty.call(sciFunc, value)) {
      setDisplayEXP(displayEXP + value);
      setExpression(expression + sciFunc[value]);
    } else if (value === '!') {
      const lastNum = extractLastNum(expression);

      if (lastNum != null) {
        const num = parseFloat(lastNum);
        setDisplayEXP(displayEXP + value);
        setExpression(expression.replace(lastNum, factorial(num).toString()));
      }
    } else {
      setExpression(expression + value);
      setDisplayEXP(displayEXP + value);
    }
  }

  function extractLastNum(exp: string): string | null {
    const numbers = exp.match(/\d+/g);
    return numbers ? numbers[numbers.length - 1] : null;
  }

  return (
    <div className={styles.calculator}>
      {onClose && (
        <div className={styles.closeButton} onClick={onClose}>
          <CloseOutlined />
        </div>
      )}
      <DisplayWindow expression={displayEXP} result={result} />
      <KeysWindow handleButton={handleButton} />
    </div>
  );
}

export default Calculator;
